/**
 * Utilidad para traducir nombres de países y provincias a español
 * - Países: Usa i18n-iso-countries para obtener nombres oficiales
 * - Provincias de España: Mapeo manual de códigos ISO a nombres en español
 */

import countries from 'i18n-iso-countries';
import es from 'i18n-iso-countries/langs/es.json';

// Registrar el locale español
countries.registerLocale(es);

/**
 * Mapeo de códigos ISO de provincias/comunidades españolas a nombres en español
 * Fuente: country-state-city usa nombres en inglés, necesitamos traducirlos
 */
const SPAIN_STATE_TRANSLATIONS: Record<string, string> = {
  // Comunidades Autónomas
  'AN': 'Andalucía',
  'AR': 'Aragón',
  'AS': 'Asturias',
  'PM': 'Islas Baleares',
  'CN': 'Islas Canarias',
  'CB': 'Cantabria',
  'CL': 'Castilla y León',
  'CM': 'Castilla-La Mancha',
  'CT': 'Cataluña',
  'VC': 'Comunidad Valenciana',
  'EX': 'Extremadura',
  'GA': 'Galicia',
  'MD': 'Comunidad de Madrid',
  'MC': 'Región de Murcia',
  'NC': 'Comunidad Foral de Navarra',
  'PV': 'País Vasco',
  'RI': 'La Rioja',

  // Ciudades Autónomas
  'CE': 'Ceuta',
  'ML': 'Melilla',

  // Provincias (en country-state-city aparecen como "X Province")
  'BU': 'Burgos',
  'LE': 'León',
  'P': 'Palencia',
  'SA': 'Salamanca',
  'SG': 'Segovia',
  'SO': 'Soria',
  'VA': 'Valladolid',
  'ZA': 'Zamora',
  'AV': 'Ávila',
};

/**
 * Obtiene el nombre de un país en español basado en su código ISO
 * @param isoCode - Código ISO del país (ej: 'ES', 'MX', 'AR')
 * @param fallbackName - Nombre en inglés como fallback si no se encuentra traducción
 * @returns Nombre del país en español
 */
export const getCountryNameInSpanish = (isoCode: string, fallbackName?: string): string => {
  const spanishName = countries.getName(isoCode, 'es');
  return spanishName || fallbackName || isoCode;
};

/**
 * Obtiene todos los países con sus nombres en español
 * @returns Objeto con códigos ISO como keys y nombres en español como values
 */
export const getAllCountriesInSpanish = (): Record<string, string> => {
  return countries.getNames('es');
};

/**
 * Obtiene el nombre de una provincia/comunidad española en español
 * @param countryCode - Código ISO del país (debe ser 'ES' para España)
 * @param stateCode - Código ISO de la provincia/comunidad (ej: 'AS', 'CT', 'MD')
 * @param fallbackName - Nombre en inglés como fallback si no se encuentra traducción
 * @returns Nombre de la provincia/comunidad en español
 */
export const getStateNameInSpanish = (
  countryCode: string,
  stateCode: string,
  fallbackName?: string
): string => {
  // Solo traducir provincias españolas por ahora
  if (countryCode === 'ES') {
    return SPAIN_STATE_TRANSLATIONS[stateCode] || fallbackName || stateCode;
  }

  // Para otros países, devolver el nombre original
  return fallbackName || stateCode;
};
